import { Drawer, DrawerContent } from './ui/drawer';
import { useOnboardingStore } from '@stores/onboardingStore';
import { onboardingFlows, OnboardingStep, type OnboardingAction } from '@config/onboarding-flows';
import { markCompleted } from '@utils/onboardingProgress';
import { OnboardingCarousel } from './OnboardingCarousel';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// NOTE: This component is mounted outside <RouterProvider> in index.tsx.
// It cannot use Router hooks (useNavigate, useLocation, etc.).
// Action callbacks are closures from consumer components, which have router context.
export const GlobalOnboarding = () => {
  const isOpen = useOnboardingStore(state => state.isOpen);
  const storedFlow = useOnboardingStore(state => state.flow);
  const closeOnboarding = useOnboardingStore(state => state.closeOnboarding);
  const setBackHandler = useOnboardingStore(state => state.setBackHandler);

  const flowId = storedFlow?.flowId;
  const variant = storedFlow?.variant;
  const actions = storedFlow?.actions;
  const contextId = storedFlow?.contextId;

  const actionsKey = Object.keys(actions ?? {})
    .sort()
    .join(',');
  const flowKey = `${flowId}:${variant}:${contextId ?? ''}:${actionsKey}`;

  const { trackEvent } = useAmplitudeTracking();

  // Resolve flow and variant from config
  const flow = flowId ? onboardingFlows[flowId] : null;
  const flowVariant = flow && variant ? flow.variants[variant] : null;
  const steps = flowVariant?.steps ?? [];
  const dismissible = flow?.dismissible ?? false;

  // caching flowKey as a backup in case the animation handler doesn't fire
  // alternatively if we don't want to depend on vaul's onAnimationEnd
  // we can use setTimeout to clear this variable
  const [cachedSteps, setCachedSteps] = useState<[string, OnboardingStep[]] | null>(null);

  if (isOpen && steps.length > 0 && (!cachedSteps || cachedSteps[0] != flowKey)) {
    setCachedSteps([flowKey, steps]);
  }

  // Track onboarding_shown (once per unique flow identity, ref-guarded)
  const trackedFlowKeyRef = useRef<string | null>(null);
  const stepsLength = steps.length;
  useEffect(() => {
    if (!isOpen || !flowId || !variant || stepsLength === 0) {
      if (!isOpen) {
        trackedFlowKeyRef.current = null;
      }
      return;
    }
    if (trackedFlowKeyRef.current === flowKey) return;
    trackedFlowKeyRef.current = flowKey;
    trackEvent(ANALYTICS_EVENTS.onboarding_shown, {
      flow_id: flowId,
      variant,
      context_id: contextId ?? null,
      total_steps: stepsLength,
    });
  }, [isOpen, flowId, variant, contextId, stepsLength, flowKey, trackEvent]);

  const handleComplete = useCallback(() => {
    if (flowId && variant) {
      trackEvent(ANALYTICS_EVENTS.onboarding_completed, {
        flow_id: flowId,
        variant,
        context_id: contextId ?? null,
        total_steps: stepsLength,
      });
      markCompleted(flowId, variant, contextId);
    }
    closeOnboarding('user');
  }, [flowId, variant, contextId, stepsLength, closeOnboarding, trackEvent]);

  const handleDismiss = useCallback(
    (stepIndex: number) => {
      if (flowId && variant) {
        trackEvent(ANALYTICS_EVENTS.onboarding_dismissed, {
          flow_id: flowId,
          variant,
          context_id: contextId ?? null,
          step_index: stepIndex,
          total_steps: stepsLength,
        });
        markCompleted(flowId, variant, contextId);
      }
      closeOnboarding('user');
    },
    [flowId, variant, contextId, stepsLength, closeOnboarding, trackEvent],
  );

  const handleStepAction = useCallback(
    (action: OnboardingAction, stepIndex: number) => {
      if (flowId && variant) {
        trackEvent(ANALYTICS_EVENTS.onboarding_action_clicked, {
          flow_id: flowId,
          variant,
          action,
          step_index: stepIndex,
          context_id: contextId ?? null,
        });
        // CTA on the last step counts as completing the flow
        if (stepIndex === stepsLength - 1) {
          trackEvent(ANALYTICS_EVENTS.onboarding_completed, {
            flow_id: flowId,
            variant,
            context_id: contextId ?? null,
            total_steps: stepsLength,
          });
        }
        // Mark completed BEFORE calling action (action may navigate away)
        markCompleted(flowId, variant, contextId);
      }
      // Don't close the drawer -- the action (e.g. signIn redirect) will
      // navigate away, and the carousel shows a loading spinner meanwhile.
      // The Zustand store has no persist middleware, so isOpen resets on reload.

      // Fire-and-forget: consumer owns error handling
      const actionFn = actions?.[action];
      if (actionFn) {
        actionFn();
      }
    },
    [flowId, variant, contextId, stepsLength, actions, trackEvent],
  );

  // Track current step for accurate swipe/backdrop dismiss analytics
  const currentStepRef = useRef(0);
  useEffect(() => {
    currentStepRef.current = 0;
  }, [flowKey]);

  const handleStepChange = useCallback((stepIndex: number) => {
    currentStepRef.current = stepIndex;
  }, []);

  // Dismissible flows: route drawer close through dismiss handler
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && dismissible) {
        handleDismiss(currentStepRef.current);
      }
    },
    [dismissible, handleDismiss],
  );

  useLayoutEffect(() => {
    // Non-dismissible: register null so the store fallback closes without
    // markCompleted (§6.8).
    setBackHandler(
      dismissible
        ? () => {
            handleDismiss(currentStepRef.current);
          }
        : null,
    );
    return () => {
      setBackHandler(null);
    };
  }, [setBackHandler, dismissible, handleDismiss]);

  return (
    <Drawer
      open={isOpen && steps.length > 0}
      // AIDEV-TODO: swipe/backdrop still trap non-dismissible flows on CTA failure; Android back closes without completing, but web has no equivalent escape.
      // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional: non-dismissible flows must not close via swipe/backdrop
      onOpenChange={dismissible ? handleOpenChange : () => {}}
      dismissible={dismissible}
    >
      <DrawerContent
        // for some reason onAnimation end on Drawer doesn't trigger, but this
        // one on DrawerContent (which shouldn't have it) does trigger
        onAnimationEnd={() => {
          setCachedSteps(null);
        }}
        className="bg-mb-offwhite [--mb-surface:var(--color-mb-offwhite)]"
        hideHandle={!dismissible}
      >
        {cachedSteps ? (
          <OnboardingCarousel
            key={flowKey}
            steps={cachedSteps[1]}
            dismissible={dismissible}
            onComplete={handleComplete}
            onDismiss={handleDismiss}
            onStepAction={handleStepAction}
            onStepChange={handleStepChange}
          />
        ) : (
          <div> </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
